<script>
  export let question;
  export let updateWorkingQuestion;
  
  let isExpanded = false;
  let jsonError = null;
  let showValidation = false;
  let viewMode = 'dataset'; // 'dataset' or 'full'
  
  function toggleExpanded() {
    isExpanded = !isExpanded;
    if (isExpanded) {
      validateDataset();
    }
  }
  
  function switchViewMode(mode) {
    viewMode = mode;
    jsonError = null;
  }
  
  function handleDatasetChange(event) {
    const value = event.target.value;
    try {
      const dataset = JSON.parse(value);
      
      // Basic validation
      if (!Array.isArray(dataset)) {
        throw new Error('Dataset must be an array');
      }
      
      // Validate each handler
      dataset.forEach((handler, index) => {
        if (!Array.isArray(handler)) {
          throw new Error(`Handler ${index} must be an array`);
        }
        if (handler.length < 1) {
          throw new Error(`Handler ${index} must have at least a type`);
        }
        if (typeof handler[0] !== 'string') {
          throw new Error(`Handler ${index} type must be a string`);
        }
      });
      
      updateWorkingQuestion('dataset', dataset);
      jsonError = null;
    } catch (error) {
      jsonError = error.message;
    }
  }
  
  function handleFullQuestionChange(event) {
    const value = event.target.value;
    try {
      const fullQuestion = JSON.parse(value);
      
      // Basic validation - ensure it's an object
      if (typeof fullQuestion !== 'object' || fullQuestion === null) {
        throw new Error('Question must be a valid JSON object');
      }
      
      // Update the entire question
      Object.keys(fullQuestion).forEach(key => {
        updateWorkingQuestion(key, fullQuestion[key]);
      });
      
      jsonError = null;
    } catch (error) {
      jsonError = error.message;
    }
  }
  
  function validateDataset() {
    showValidation = true;
    const validation = getDatasetValidation();
    return validation;
  }
  
  function getDatasetValidation() {
    const dataset = question.dataset || [];
    const validation = {
      isValid: true,
      warnings: [],
      errors: [],
      info: []
    };
    
    // Check for required handlers
    const handlerTypes = dataset.map(handler => handler[0]);
    
    // Check for self node setup
    const hasSelf = handlerTypes.includes('setSelf');
    const hasSelfProps = handlerTypes.includes('selfProps');
    const hasNodes = handlerTypes.includes('nodes');
    const hasLinks = handlerTypes.includes('links');
    
    if (hasSelf && !hasSelfProps) {
      validation.warnings.push('setSelf handler found but no selfProps - self node will have no properties');
    }
    
    if (hasNodes && !hasLinks) {
      validation.info.push('nodes handler found but no links - nodes will be isolated');
    }
    
    if (hasLinks && !hasNodes && !hasSelf) {
      validation.errors.push('links handler found but no nodes or setSelf - links need nodes to connect');
      validation.isValid = false;
    }
    
    // Check each handler for validity
    dataset.forEach((handler, index) => {
      const [type, props, extra] = handler;
      
      switch (type) {
        case 'setSelf':
          if (props) {
            validation.warnings.push(`setSelf handler ${index} has unexpected properties`);
          }
          break;
          
        case 'selfProps':
          if (!props || typeof props !== 'object') {
            validation.errors.push(`selfProps handler ${index} requires properties object`);
            validation.isValid = false;
          }
          break;
          
        case 'nodes':
          if (!props || typeof props !== 'object') {
            validation.errors.push(`nodes handler ${index} requires properties object`);
            validation.isValid = false;
          } else if (!props.type) {
            validation.warnings.push(`nodes handler ${index} should specify a type property`);
          }
          break;
          
        case 'links':
          if (!props || !Array.isArray(props)) {
            validation.errors.push(`links handler ${index} requires an array of link configurations`);
            validation.isValid = false;
          } else {
            props.forEach((link, linkIndex) => {
              if (!link.source || !link.target) {
                validation.errors.push(`Link ${linkIndex} in handler ${index} missing source or target`);
                validation.isValid = false;
              }
            });
          }
          break;
          
        case 'nodeProps':
          if (!props || typeof props !== 'object') {
            validation.errors.push(`nodeProps handler ${index} requires properties object`);
            validation.isValid = false;
          }
          if (!extra) {
            validation.warnings.push(`nodeProps handler ${index} should specify target question`);
          }
          break;
          
        default:
          validation.warnings.push(`Unknown handler type "${type}" at index ${index}`);
      }
    });
    
    return validation;
  }
  
  function formatJson() {
    try {
      if (viewMode === 'dataset') {
        const formatted = JSON.stringify(question.dataset || [], null, 2);
        updateWorkingQuestion('dataset', JSON.parse(formatted));
      } else {
        // Format the entire question object
        const formatted = JSON.stringify(question, null, 2);
        const parsedQuestion = JSON.parse(formatted);
        Object.keys(parsedQuestion).forEach(key => {
          updateWorkingQuestion(key, parsedQuestion[key]);
        });
      }
      jsonError = null;
    } catch (error) {
      jsonError = 'Failed to format JSON';
    }
  }
  
  // Reactive derived state - automatically updates when question changes
  $: currentJson = viewMode === 'dataset' 
    ? JSON.stringify(question.dataset || [], null, 2)
    : JSON.stringify(question, null, 2);
  $: validation = showValidation ? getDatasetValidation() : null;
</script>

<div class="raw-dataset-editor">
  <div class="editor-header" on:click={toggleExpanded} role="button" tabindex="0">
    <h6>
      <span class="expand-icon" class:expanded={isExpanded}>
        {isExpanded ? '▼' : '▶'}
      </span>
      JSON Editor
      <span class="danger-badge">ADVANCED</span>
    </h6>
    <p class="editor-description">
      {isExpanded ? 'Edit JSON configuration (dataset or full question)' : 'Click to expand JSON editor'}
    </p>
  </div>
  
  {#if isExpanded}
    <div class="editor-content">
      
      <!-- View Mode Toggle -->
      <div class="view-mode-toggle">
        <h6>View Mode</h6>
        <div class="toggle-buttons">
          <button 
            class="btn btn-toggle" 
            class:active={viewMode === 'dataset'}
            on:click={() => switchViewMode('dataset')}
          >
            Dataset Only
          </button>
          <button 
            class="btn btn-toggle" 
            class:active={viewMode === 'full'}
            on:click={() => switchViewMode('full')}
          >
            Full Question
          </button>
        </div>
      </div>
      
      <!-- Quick Actions -->
      <div class="quick-actions">
        <h6>Quick Actions</h6>
        <div class="action-buttons">
          <button class="btn btn-secondary btn-small" on:click={formatJson}>
            Format JSON
          </button>
          {#if viewMode === 'dataset'}
            <button class="btn btn-secondary btn-small" on:click={validateDataset}>
              Validate Dataset
            </button>
          {/if}
        </div>
      </div>
      
      <!-- JSON Editor -->
      <div class="json-editor-wrapper">
        <label for="json-editor">
          {viewMode === 'dataset' ? 'Dataset JSON Configuration' : 'Full Question JSON'}
        </label>
        <textarea 
          id="json-editor"
          value={currentJson}
          on:input={viewMode === 'dataset' ? handleDatasetChange : handleFullQuestionChange}
          rows="25"
          class="json-editor"
          class:error={jsonError}
          spellcheck="false"
        ></textarea>
        
        {#if jsonError}
          <div class="json-error">
            <span class="error-icon">❌</span>
            <span class="error-message">{jsonError}</span>
          </div>
        {/if}
      </div>
      
      <!-- Validation Results (only for dataset view) -->
      {#if validation && viewMode === 'dataset'}
        <div class="validation-results">
          <div class="validation-header">
            <h6>
              Validation Results
              <span class="validation-status" class:valid={validation.isValid} class:invalid={!validation.isValid}>
                {validation.isValid ? '✅ VALID' : '❌ INVALID'}
              </span>
            </h6>
          </div>
          
          {#if validation.errors.length > 0}
            <div class="validation-section errors">
              <h6>❌ Errors ({validation.errors.length})</h6>
              <ul>
                {#each validation.errors as error}
                  <li>{error}</li>
                {/each}
              </ul>
            </div>
          {/if}
          
          {#if validation.warnings.length > 0}
            <div class="validation-section warnings">
              <h6>⚠️ Warnings ({validation.warnings.length})</h6>
              <ul>
                {#each validation.warnings as warning}
                  <li>{warning}</li>
                {/each}
              </ul>
            </div>
          {/if}
          
          {#if validation.info.length > 0}
            <div class="validation-section info">
              <h6>ℹ️ Info ({validation.info.length})</h6>
              <ul>
                {#each validation.info as info}
                  <li>{info}</li>
                {/each}
              </ul>
            </div>
          {/if}
        </div>
      {/if}
      
      {#if viewMode === 'dataset'}
        <!-- Documentation -->
        <div class="dataset-documentation">
          <h6>Dataset Handler Reference</h6>
          <div class="handler-docs">
            <div class="handler-doc">
              <code>["setSelf"]</code>
              <p>Creates the self node (main entity)</p>
            </div>
            <div class="handler-doc">
              <code>["selfProps", {"{"}props{"}"}]</code>
              <p>Adds properties to the self node</p>
            </div>
            <div class="handler-doc">
              <code>["nodes", {"{"}props{"}"}]</code>
              <p>Creates nodes from answers</p>
            </div>
            <div class="handler-doc">
              <code>["links", [link_configs]]</code>
              <p>Creates links between nodes</p>
            </div>
            <div class="handler-doc">
              <code>["nodeProps", {"{"}props{"}"}, target_question]</code>
              <p>Adds properties to existing nodes</p>
            </div>
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  .raw-dataset-editor {
    border: 2px solid #f59e0b;
    border-radius: 8px;
    margin-bottom: 20px;
    background: #fffbeb;
    overflow: hidden;
  }
  
  .editor-header {
    padding: 15px;
    cursor: pointer;
    border-bottom: 1px solid #fcd34d;
    transition: background-color 0.2s ease;
    
    &:hover {
      background: #fef3c7;
    }
    
    h6 {
      margin: 0 0 5px 0;
      font-size: 0.8rem;
      font-weight: bold;
      color: #92400e;
      display: flex;
      align-items: center;
      gap: 8px;
      
      .expand-icon {
        transition: transform 0.2s ease;
        
        &.expanded {
          transform: rotate(0deg);
        }
      }
    }
    
    .editor-description {
      margin: 0;
      font-size: 0.7rem;
      color: #78350f;
      font-style: italic;
    }
    
    .danger-badge {
      background: #dc2626;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 9px;
      font-weight: bold;
      letter-spacing: 0.5px;
    }
  }
  
  .editor-content {
    padding: 20px;
    background: #fff;
  }
  
  .view-mode-toggle {
    margin-bottom: 20px;
    padding: 12px;
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    
    h6 {
      margin: 0 0 10px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #374151;
    }
    
    .toggle-buttons {
      display: flex;
      gap: 8px;
    }
    
    .btn-toggle {
      padding: 6px 12px;
      background: #e2e8f0;
      color: #64748b;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      font-size: 0.75rem;
      
      &:hover {
        background: #cbd5e1;
      }
      
      &.active {
        background: #3b82f6;
        color: white;
        border-color: #2563eb;
      }
    }
  }
  
  .quick-actions {
    margin-bottom: 20px;
    padding: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    
    h6 {
      margin: 0 0 10px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #374151;
    }
    
    .action-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
  }
  
  .json-editor-wrapper {
    margin-bottom: 20px;
    
    label {
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
      font-size: 0.75rem;
      color: #374151;
    }
    
    .json-editor {
      width: 100%;
      padding: 15px;
      border: 2px solid #d1d5db;
      border-radius: 6px;
      background: #f9fafb;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 12px;
      line-height: 1.5;
      resize: vertical;
      min-height: 400px;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        background: #fff;
      }
      
      &.error {
        border-color: #ef4444;
        background: #fef2f2;
      }
    }
    
    .json-error {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
      padding: 8px 12px;
      background: #fef2f2;
      border: 1px solid #fca5a5;
      border-radius: 4px;
      
      .error-icon {
        font-size: 0.8rem;
      }
      
      .error-message {
        font-size: 0.75rem;
        color: #dc2626;
        font-weight: 500;
      }
    }
  }
  
  .validation-results {
    margin-bottom: 20px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    
    .validation-header {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;
      
      h6 {
        margin: 0;
        font-size: 0.75rem;
        font-weight: bold;
        color: #374151;
        display: flex;
        align-items: center;
        gap: 8px;
        
        .validation-status {
          padding: 2px 6px;
          border-radius: 10px;
          font-size: 9px;
          font-weight: bold;
          letter-spacing: 0.5px;
          
          &.valid {
            background: #dcfce7;
            color: #166534;
          }
          
          &.invalid {
            background: #fef2f2;
            color: #dc2626;
          }
        }
      }
    }
    
    .validation-section {
      padding: 12px;
      
      &:not(:last-child) {
        border-bottom: 1px solid #f3f4f6;
      }
      
      h6 {
        margin: 0 0 8px 0;
        font-size: 0.7rem;
        font-weight: bold;
      }
      
      ul {
        margin: 0;
        padding-left: 15px;
        
        li {
          font-size: 0.7rem;
          margin-bottom: 4px;
          line-height: 1.3;
          
          &:last-child {
            margin-bottom: 0;
          }
        }
      }
      
      &.errors {
        h6, li {
          color: #dc2626;
        }
      }
      
      &.warnings {
        h6, li {
          color: #d97706;
        }
      }
      
      &.info {
        h6, li {
          color: #2563eb;
        }
      }
    }
  }
  
  .dataset-documentation {
    padding: 15px;
    background: #f0f9ff;
    border: 1px solid #0ea5e9;
    border-radius: 6px;
    
    h6 {
      margin: 0 0 12px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #0c4a6e;
    }
    
    .handler-docs {
      display: grid;
      gap: 10px;
      
      .handler-doc {
        display: flex;
        align-items: center;
        gap: 10px;
        
        code {
          background: #e0f2fe;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 0.65rem;
          color: #0c4a6e;
          font-weight: bold;
          min-width: 200px;
        }
        
        p {
          margin: 0;
          font-size: 0.7rem;
          color: #075985;
        }
      }
    }
  }
  
  .btn {
    padding: 4px 8px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-secondary {
      background: #10b981;
      color: white;
      
      &:hover {
        background: #059669;
      }
    }
    
    &.btn-small {
      padding: 3px 6px;
      font-size: 10px;
    }
  }
</style>